import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import SuccessAnimation from '../components/SuccessAnimation';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase/config';
import { RegistrationFormData, UserType } from '../types/user';
import RegistrationForm from '../components/RegistrationForm';
import { Container, Box } from '@mui/material';

interface RouteParams {
  type: UserType;
}

const RegisterForm: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { type } = useParams<RouteParams>();
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (formData: RegistrationFormData) => {
    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const userId = userCredential.user.uid;
      let profilePhotoUrl = '';

      // Upload photos and documents if provided
      let backgroundPhotoUrl = '';
      let documentUrls: string[] = [];
      
      try {
        // Validate and upload profile photo
        if (formData.profilePhoto) {
          if (!formData.profilePhoto.type.match('image/(jpeg|png|gif)')) {
            throw new Error('Profile photo must be a JPEG, PNG, or GIF image');
          }
          if (formData.profilePhoto.size > 5 * 1024 * 1024) {
            throw new Error('Profile photo must be less than 5MB');
          }
          
          console.log('Uploading profile photo...');
          const profileRef = ref(storage, `profile-photos/${userId}`);
          await uploadBytes(profileRef, formData.profilePhoto);
          profilePhotoUrl = await getDownloadURL(profileRef);
          console.log('Profile photo uploaded successfully');
        }

        // Validate and upload background photo
        if (formData.backgroundPhoto) {
          if (!formData.backgroundPhoto.type.match('image/(jpeg|png|gif)')) {
            throw new Error('Background photo must be a JPEG, PNG, or GIF image');
          }
          if (formData.backgroundPhoto.size > 5 * 1024 * 1024) {
            throw new Error('Background photo must be less than 5MB');
          }
          
          console.log('Uploading background photo...');
          const backgroundRef = ref(storage, `background-photos/${userId}`);
          await uploadBytes(backgroundRef, formData.backgroundPhoto);
          backgroundPhotoUrl = await getDownloadURL(backgroundRef);
          console.log('Background photo uploaded successfully');
        }

        // Upload documents if provided
        if (formData.documents && formData.documents.length > 0) {
          const docPath = formData.type === 'company' ? 'company-docs' : 'contractor-docs';
          for (let i = 0; i < formData.documents.length; i++) {
            const document = formData.documents[i];
            
            // Validate document
            if (!document.type.match('(application/pdf|image/.*)')) {
              throw new Error('Documents must be PDF or image files');
            }
            if (document.size > 5 * 1024 * 1024) {
              throw new Error('Documents must be less than 5MB');
            }
            
            console.log(`Uploading document ${i + 1}/${formData.documents.length}...`);
            const documentRef = ref(storage, `${docPath}/${userId}/${document.name}`);
            await uploadBytes(documentRef, document);
            const documentUrl = await getDownloadURL(documentRef);
            documentUrls.push(documentUrl);
            console.log(`Document ${i + 1} uploaded successfully`);
          }
        }
      } catch (uploadError: any) {
        console.error('Upload error:', uploadError);
        throw new Error(uploadError.message || 'Error uploading files');
      }

      // Prepare user data based on type
      const { password, profilePhoto, backgroundPhoto, documents, ...restFormData } = formData;
      
      const now = Timestamp.now();
      // Ensure all required BaseUser fields are present
      const userData = {
        id: userId,
        email: formData.email,
        type: formData.type,
        profilePhotoUrl,
        backgroundPhotoUrl,
        address: formData.address || '',
        city: formData.city || '',
        postalCode: formData.postalCode || '',
        country: formData.country || '',
        phone: formData.phone || '',
        professionalEmail: formData.professionalEmail || '',
        website: formData.website || '',
        createdAt: now,
        updatedAt: now,
        emailVerified: false,
        ...restFormData,
        documents: documentUrls
      };

      // Log the data being saved
      console.log('Attempting to save user data:', userData);

      try {
        // Save user data to Firestore
        console.log('Starting Firestore save operation...');
        const userRef = doc(db, 'users', userId);
        console.log('User reference created:', userRef.path);
        
        await setDoc(userRef, userData);
        console.log('Successfully saved user data to path:', userRef.path);
      } catch (firestoreError) {
        console.error('Firestore save error:', firestoreError);
        console.error('Error details:', {
          code: firestoreError.code,
          message: firestoreError.message,
          stack: firestoreError.stack
        });
        throw firestoreError;
      }

      // Send verification email
      await sendEmailVerification(userCredential.user);

      // Show success animation
      setShowSuccess(true);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const handleSuccessFinish = () => {
    history.push('/email-verification');
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box sx={{ position: 'relative' }}>
            <RegistrationForm
              userType={type}
              onSubmit={handleSubmit}
            />
            {showSuccess && <SuccessAnimation onFinish={handleSuccessFinish} />}
          </Box>
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default RegisterForm;
